import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Database, Server, CheckCircle, XCircle, Activity, X, RefreshCw } from 'lucide-react';
import apiService from '../../services/api';

const DatabaseSettings = () => {
  const { t } = useTranslation();
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDb, setEditingDb] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    server: '',
    databaseName: '',
    username: '',
    password: '',
    isActive: true,
    isDefault: false
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await apiService.request('/admin/settings/databases');
      setDatabases(data);
    } catch (err) {
      console.error('Failed to load databases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDb(null);
    setFormData({
      title: '',
      server: '',
      databaseName: '',
      username: '',
      password: '',
      isActive: true,
      isDefault: false
    });
    setError('');
    setTestResult(null);
    setShowModal(true);
  };

  const handleEdit = (db) => {
    setEditingDb(db);
    setFormData({
      title: db.title,
      server: db.server,
      databaseName: db.databaseName,
      username: db.username,
      password: '',
      isActive: db.isActive,
      isDefault: db.isDefault
    });
    setError('');
    setTestResult(null);
    setShowModal(true);
  };

  const handleDelete = async (db) => {
    if (!window.confirm(t('admin.databases.confirmDelete', { name: db.title }))) return;

    try {
      await apiService.request(`/admin/settings/databases/${db.id}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      alert(t('admin.databases.deleteFailed') + ': ' + (err.message || t('admin.common.error')));
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await apiService.request('/admin/settings/databases/test', {
        method: 'POST',
        body: JSON.stringify({
          server: formData.server,
          databaseName: formData.databaseName,
          username: formData.username,
          password: formData.password
        })
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err.message || t('admin.common.error') });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingDb) {
        await apiService.request(`/admin/settings/databases/${editingDb.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      } else {
        await apiService.request('/admin/settings/databases', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message || t('admin.common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <RefreshCw size={24} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h3 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>{t('admin.databases.title')}</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
            {t('admin.databases.subtitle')}
          </p>
        </div>
        <button
          onClick={handleAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: '#7c3aed',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <Plus size={16} />
          {t('admin.databases.newConnection')}
        </button>
      </div>

      {/* Database List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {databases.map(db => (
          <div
            key={db.id}
            style={{
              padding: '16px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                background: db.isActive
                  ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                  : '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                marginRight: '12px'
              }}>
                <Database size={22} />
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{db.title}</span>
                  {db.isDefault && (
                    <span style={{
                      padding: '2px 8px',
                      background: '#fef3c7',
                      color: '#d97706',
                      fontSize: '10px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      {t('admin.databases.default')}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                  <Server size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  {db.server}
                </div>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              fontSize: '13px',
              marginBottom: '12px'
            }}>
              <div>
                <span style={{ color: '#9ca3af' }}>{t('admin.databases.database')}:</span>
                <span style={{ marginLeft: '8px', color: '#1f2937', fontWeight: '500' }}>{db.databaseName}</span>
              </div>
              <div>
                <span style={{ color: '#9ca3af' }}>{t('admin.databases.user')}:</span>
                <span style={{ marginLeft: '8px', color: '#1f2937', fontWeight: '500' }}>{db.username}</span>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '12px',
              borderTop: '1px solid #f3f4f6'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {db.isActive ? (
                  <>
                    <CheckCircle size={14} color="#10b981" />
                    <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '500' }}>{t('admin.databases.active')}</span>
                  </>
                ) : (
                  <>
                    <XCircle size={14} color="#9ca3af" />
                    <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>{t('admin.databases.inactive')}</span>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleEdit(db)}
                  style={{
                    padding: '6px 12px',
                    background: '#f3f4f6',
                    borderRadius: '6px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Edit size={14} />
                  {t('admin.databases.edit')}
                </button>
                <button
                  onClick={() => handleDelete(db)}
                  style={{
                    padding: '6px 12px',
                    background: '#fef2f2',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Trash2 size={14} />
                  {t('admin.databases.delete')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {databases.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <Database size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>{t('admin.databases.empty')}</p>
          <button
            onClick={handleAdd}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              background: '#7c3aed',
              color: 'white',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            {t('admin.databases.addFirst')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontWeight: '600' }}>
                {editingDb ? t('admin.databases.editConnection') : t('admin.databases.newConnectionTitle')}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ padding: '4px' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
              {error && (
                <div style={{
                  padding: '12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.databases.connectionName')} *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder={t('admin.databases.connectionNamePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.databases.server')} *
                </label>
                <input
                  type="text"
                  value={formData.server}
                  onChange={(e) => setFormData({ ...formData, server: e.target.value })}
                  required
                  placeholder={t('admin.databases.serverPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.databases.databaseName')} *
                </label>
                <input
                  type="text"
                  value={formData.databaseName}
                  onChange={(e) => setFormData({ ...formData, databaseName: e.target.value })}
                  required
                  placeholder={t('admin.databases.databaseNamePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.databases.username')} *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder={t('admin.databases.usernamePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('admin.databases.password')} {!editingDb && '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingDb}
                  placeholder={editingDb ? t('admin.databases.passwordHint') : ''}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px' }}>{t('admin.databases.active')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px' }}>{t('admin.databases.default')}</span>
                </label>
              </div>

              {/* Test Result */}
              {testResult && (
                <div style={{
                  padding: '12px',
                  background: testResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {testResult.success ? (
                    <CheckCircle size={18} color="#10b981" />
                  ) : (
                    <XCircle size={18} color="#dc2626" />
                  )}
                  <span style={{
                    fontSize: '13px',
                    color: testResult.success ? '#166534' : '#dc2626'
                  }}>
                    {testResult.message}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing || !formData.server || !formData.databaseName || !formData.username}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: testing ? 0.6 : 1
                  }}
                >
                  <Activity size={14} />
                  {testing ? t('admin.databases.testing') : t('admin.databases.testConnection')}
                </button>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('admin.databases.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 16px',
                    background: saving ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {saving ? t('admin.databases.saving') : t('admin.databases.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseSettings;
